import React, { useContext } from "react";
import { Paper, Typography, Link as MuiLink } from "@mui/material";
import SectionWrapper from "./SectionPage/components/Section/SectionWrapper";
import { BNBox, BNButton, BNListItem, BNRichText, dateFormatter, resolveUrl } from "@bn-monorepo/js-packages";
import Media from "../CmsComponents/Media/Media";
import { Link, Link as LinkRouter } from "react-router";
import { SettingsContext } from "../../settingsContext";

interface Props {
  cmsContent: {
    displayDate: boolean;
    showCategoryTag: boolean;
    numberOfNewestToShow: number;
    groups: Array<{
      name: string;
      url: string;
    }>;
    articleList: ArticleSummary[];
    articleListPopular: ArticleSummary[];
    heading: string;
    headingCategories: string;
    headingNewest: string;
    headingPopular: string;
    headingArticleList: string;
    readMoreLabel: string;
  };
}

export interface ArticleImage {
  url: string;
  alt: string;
}

export interface ArticleSummary {
  key: string;
  image: ArticleImage;
  name: string;
  metaDescription: string;
  showPublishedDate: boolean;
  url: string;
  date: Date;
  group: {
    name: string;
    url: string;
  };
}

const Articles: React.FC<Props> = ({ cmsContent }) => {
  const {
    displayDate,
    showCategoryTag,
    numberOfNewestToShow = 0,
    groups,
    articleList,
    articleListPopular,
    heading,
    headingCategories,
    headingNewest,
    headingPopular,
    headingArticleList,
    readMoreLabel,
  } = cmsContent;

  const latestArticles = [...(articleList ?? [])]
    .sort(
      (a, b) =>
        new Date(b.date instanceof Date ? b.date : String(b.date)).getTime() -
        new Date(a.date instanceof Date ? a.date : String(a.date)).getTime(),
    )
    .slice(0, numberOfNewestToShow);

  return (
    <SectionWrapper backgroundColor="transparent" anchorId="" noImage>
      <Typography
        component={"h1"}
        variant={"h4"}
        sx={{
          fontWeight: "bold",
          m: 2,
        }}
      >
        {heading}
      </Typography>
      {groups.length > 0 && (
        <>
          <Typography
            component={"h2"}
            variant={"h6"}
            sx={{
              fontWeight: "bold",
              m: 2,
            }}
          >
            {headingCategories}
          </Typography>
          <BNBox
            sx={{
              mx: 2,
              gap: 2,
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(auto-fill, minmax(min(15rem, 100%), 1fr))",
                md: "repeat(auto-fill, minmax(min(28.5rem, 100%), 1fr))",
              },
            }}
          >
            {groups.map((x) => (
              <BNListItem to={resolveUrl(x.url)} key={x.url}>
                {x.name}
              </BNListItem>
            ))}
          </BNBox>
        </>
      )}

      {!!latestArticles?.length && (
        <>
          <Typography
            component={"h2"}
            variant={"h6"}
            sx={{
              fontWeight: "bold",
              m: 2,
            }}
          >
            {headingNewest}
          </Typography>
          <BNBox
            sx={{
              mx: 2,
              display: "grid",
              gap: 2,
              justifyContent: "center",
              gridTemplateColumns: {
                xs: "repeat(auto-fill, minmax(min(17rem, 100%), 1fr))",
                md: "repeat(auto-fill, minmax(min(25rem, 100%), 1fr))",
              },
            }}
          >
            {latestArticles.map((card, index) =>
              ArticleItem(card, index, showCategoryTag, readMoreLabel, displayDate, true),
            )}
          </BNBox>
        </>
      )}

      {!!articleListPopular?.length && (
        <>
          <Typography
            component={"h2"}
            variant={"h6"}
            sx={{
              fontWeight: "bold",
              m: 2,
            }}
          >
            {headingPopular}
          </Typography>
          <BNBox
            sx={{
              mx: 2,
              display: "grid",
              gap: 2,
              justifyContent: "center",
              gridTemplateColumns: {
                xs: "repeat(auto-fill, minmax(min(17rem, 100%), 1fr))",
                md: "repeat(auto-fill, minmax(min(25rem, 100%), 1fr))",
              },
            }}
          >
            {articleListPopular.map((card, index) =>
              ArticleItem(card, index, showCategoryTag, readMoreLabel, displayDate, true),
            )}
          </BNBox>
        </>
      )}
      <Typography
        component={"h2"}
        variant={"h6"}
        sx={{
          fontWeight: "bold",
          m: 2,
        }}
      >
        {headingArticleList}
      </Typography>
      <BNBox
        sx={{
          mx: 2,
          display: "grid",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 2,
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
        }}
      >
        {articleList?.map((card, index) => ArticleItem(card, index, showCategoryTag, readMoreLabel, displayDate))}
      </BNBox>
    </SectionWrapper>
  );
};
export default Articles;
function ArticleItem(
  card: ArticleSummary,
  index: number,
  showCategoryTag: boolean,
  readMoreLabel: string,
  displayDate: boolean,
  hideDescription?: boolean,
) {
  const { locale } = useContext(SettingsContext);
  const formatDate = dateFormatter(locale);
  return (
    <Paper
      key={card.key}
      sx={{
        borderRadius: 4,
        display: "flex",
        width: "100%",
        overflow: "hidden",
        flexDirection: "column",
      }}
    >
      <MuiLink
        component={LinkRouter}
        to={resolveUrl(card.url)}
        sx={{ textDecoration: "none", textUnderlineOffset: "0rem" }}
        tabIndex={-1}
      >
        <Media
          AltText={card.image.alt}
          imagePath={card.image.url}
          noWrapper={true}
          width={500}
          maxWidth={500}
          height={333}
          forceEagerLoading={index == 0}
        />
      </MuiLink>
      <BNBox
        sx={{
          m: 3,
          mt: 2,
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
        }}
      >
        {showCategoryTag && card.group && (
          <BNBox sx={{ mb: 2 }}>
            <BNButton component={Link} to={resolveUrl(card.group.url)} variant="outlined" size="small">
              {card.group.name}
            </BNButton>
          </BNBox>
        )}
        <MuiLink component={LinkRouter} to={resolveUrl(card.url)} sx={{ textDecoration: "none" }} tabIndex={-1}>
          <Typography
            component={"h3"}
            variant={"h6"}
            sx={{
              fontWeight: "bold",
            }}
          >
            {card.name}
          </Typography>
        </MuiLink>
        {displayDate && <BNBox sx={{ fontSize: { xs: "1.2rem", md: "1.4rem" } }}>{formatDate(card.date)}</BNBox>}
        {!hideDescription && (
          <BNBox>
            <BNRichText html={card.metaDescription} />
          </BNBox>
        )}
        <BNButton
          component={LinkRouter}
          to={resolveUrl(card.url)}
          variant="text"
          sx={{
            mx: 0,
            px: 0,
            fontWeight: "bold",
            mt: "auto",
            alignSelf: "flex-start",
          }}
        >
          {readMoreLabel}
        </BNButton>
      </BNBox>
    </Paper>
  );
}
